// =====================================================================
// Facturation.jsx — Module Facturation (M-007 — Day 11+ 29-05-2026)
// =====================================================================
// Combo 1 — Dashboard all-in-one + DRILL-DOWN modal + OT DECISION workflow
// Permission: facturation.read.all (Silviu, Luca, Florent uniquement)
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { facturation as facturationApi, tasks as tasksApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { aujourdhuiBruxelles } from '../lib/dates.js';
import { enregistrerReponse, messageDeBlob } from '../lib/telechargement.js';

function fmtH(min) {
  const m = Math.round(min || 0);
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}
function fmtEur(n) {
  if (!n) return '—';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtDateFR(ymd) {
  if (!ymd) return '';
  const s = String(ymd).slice(0, 10);
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}
// Stockage sur dalle (22-10-2026) : instants à l'heure de Bruxelles, heures-palette → jours-palette
function fmtDateHeure(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('fr-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
const fmtJp = (heures) => ((heures || 0) / 24).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Ce qu'il faut savoir du chiffre « Stockage » du mois : estimation (mois en cours, ou terminé pas encore calculé),
// commandes sans ligne Prélèvements (échec qui dure : en rouge) ou recalcul en attente, commandes hors totaux (projet
// inactif ou interne).
function noteStockage(data) {
  const s = data.stockage || {};
  const n = [];
  if (data.kpi.totals.stockageCommandes && s.etat === 'en-cours') n.push(data.periodLock?.locked ? 'estimation (mois verrouillé avant sa fin : calculé à la fin du mois)' : 'estimation');
  if (data.kpi.totals.stockageCommandes && s.etat === 'en-attente') n.push('mois terminé pas encore calculé : estimation (calcul dans l\'heure)');
  if (s.sansLigne) n.push(`${s.sansLigne} commande(s) sans ligne Prélèvements — vérifier le type « Stockage sur dalle » et le projet (reprise chaque heure)`);
  else if (s.etat === 'incomplet') n.push('recalcul en attente (prochain passage dans l\'heure)');
  if (s.horsListe) n.push(`${s.horsListe} commande(s) sur un projet inactif ou interne, hors totaux`);
  return n.map((x) => ` · ${x}`).join('');
}
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
function labelMonth(yearMonth) {
  if (!yearMonth) return '';
  const [y, m] = yearMonth.split('-').map(Number);
  return `${MONTHS_FR[m - 1]} ${y}`;
}

export default function Facturation() {
  const { can } = useAuth();
  const canRead = can('facturation.read.all');
  const canLock = can('facturation.lock.all');

  // Mois du jour À BRUXELLES (lib/dates.js) : entre minuit et 2 h le 1er, la date
  // UTC ouvrait encore le mois précédent.
  const defaultMonth = aujourdhuiBruxelles().slice(0, 7);
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyLock, setBusyLock] = useState(false);

  // Drill-down state
  const [drilldown, setDrilldown] = useState(null); // { kind, scope, title }
  // 22-10-2026 (demande Luca) : export de TOUTES les tâches (tous projets), pour réorganiser le catalogue
  const [exportTaches, setExportTaches] = useState(false);
  const exporterTaches = async () => {
    setExportTaches(true);
    try {
      enregistrerReponse(await tasksApi.exportAll('xlsx'), 'zeus-taches.xlsx');
    } catch (e) {
      alert(await messageDeBlob(e));
    } finally {
      setExportTaches(false);
    }
  };

  const load = async (ym) => {
    setLoading(true); setError(null);
    try { setData(await facturationApi.overview(ym)); }
    catch (e) { setError(e?.response?.data?.message || e.message || 'Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canRead) load(month); }, [month, canRead]);

  // Avant tout « return » conditionnel : règle des hooks React.
  const monthOptions = useMemo(() => {
    // Compté depuis le 1er du mois : reculer d'un mois depuis le 31 sautait
    // septembre (31/09 = 01/10) et doublait octobre.
    const opts = [];
    const [y, m] = aujourdhuiBruxelles().split('-').map(Number);
    for (let i = 0; i < 13; i++) {
      const d = new Date(Date.UTC(y, m - 1 - i, 1));
      const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      opts.push({ value: ym, label: labelMonth(ym) });
    }
    return opts;
  }, []);

  if (!canRead) {
    return (
      <div style={{ padding: 24 }}>
        <h1>Facturation</h1>
        <p style={{ color: '#dc2626' }}>Module réservé à la direction (Silviu, Luca, Florent).</p>
      </div>
    );
  }

  const handleLock = async () => {
    if (!window.confirm(`Verrouiller ${labelMonth(month)} ? Plus aucune modification possible.`)) return;
    setBusyLock(true);
    try { await facturationApi.lock(month); await load(month); }
    catch (e) { alert('Erreur: ' + (e?.response?.data?.message || e.message)); }
    finally { setBusyLock(false); }
  };
  const handleUnlock = async () => {
    const reason = window.prompt(`Déverrouiller ${labelMonth(month)} ? Motif obligatoire :`, '');
    if (!reason?.trim()) return;
    setBusyLock(true);
    try { await facturationApi.unlock(month, reason.trim()); await load(month); }
    catch (e) { alert('Erreur: ' + (e?.response?.data?.message || e.message)); }
    finally { setBusyLock(false); }
  };


  const openDrill = (kind, scope, title) => setDrilldown({ kind, scope: scope || 'all', title });
  const closeDrill = () => setDrilldown(null);

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: '0 auto' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>💰 Facturation</h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Synthèse mensuelle — cliquez sur n'importe quelle valeur pour voir le détail.
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={month} onChange={(e) => setMonth(e.target.value)} style={selectStyle}>
          {monthOptions.map(o => <option key={o.value} value={o.value}>📅 {o.label}</option>)}
        </select>
        {data?.periodLock?.locked
          ? <span style={{ ...pill, background: '#1e293b', color: '#fef3c7' }}>🔒 Verrouillée</span>
          : <span style={{ ...pill, background: '#dbeafe', color: '#1e40af' }}>⏳ Période ouverte</span>}
        <button onClick={() => load(month)} style={btnGhost}>🔄 Rafraîchir</button>
        <div style={{ flex: 1 }} />
        <a href={facturationApi.exportExcelUrl(month)} download style={{ ...btnSuccess, textDecoration: 'none', display: 'inline-block' }}>📥 Export Excel</a>
        <button onClick={exporterTaches} disabled={exportTaches} style={btnGhost}
          title="Toutes les tâches de tous les projets (actives et inactives), avec leur usage dans les feuilles de temps">
          {exportTaches ? '⏳ Export…' : '📥 Tâches (Excel)'}
        </button>
        <button style={btnDanger} disabled title="Itération 3 (PDF)">📄 PDF Pack</button>
        {canLock && (data?.periodLock?.locked
          ? <button onClick={handleUnlock} disabled={busyLock} style={btnUnlock}>🔓 Déverrouiller</button>
          : <button onClick={handleLock} disabled={busyLock} style={btnLock}>🔒 Verrouiller</button>)}
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Chargement…</div>}
      {error && <div style={errorBox}>{error}</div>}

      {data && !loading && (
        <>
          {/* KPIs cliquables */}
          <div style={kpiGrid}>
            <KPI label="Clients actifs" value={data.kpi.totalProjects} sub={`${data.kpi.readyCount} prêts`} variant="success"
                 onClick={() => openDrill('ts', 'all', `Tous les TS — ${labelMonth(month)}`)} />
            <KPI label="Total TS" value={data.kpi.totals.tsHoursStr} sub={`${data.kpi.totals.tsMinutes} min`}
                 onClick={() => openDrill('ts', 'all', `Détail TS — ${labelMonth(month)}`)} />
            <KPI label="DRAFT à valider" value={data.kpi.draftTsCount} sub={data.kpi.draftTsHours}
                 variant={data.kpi.draftTsCount > 0 ? 'warn' : 'normal'}
                 onClick={() => openDrill('ts-draft', 'all', `TS DRAFT — ${labelMonth(month)}`)} />
            <KPI label="Alertes" value={data.kpi.alertsCount} sub={`${data.kpi.jfEntriesCount} JF · ${data.kpi.pendingOtDecisions} HS pending`}
                 variant={data.kpi.alertsCount > 0 ? 'alert' : 'normal'}
                 onClick={() => openDrill('alerts', 'all', `Alertes — ${labelMonth(month)}`)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 12, marginBottom: 16 }}>
            {/* ALERTES */}
            <Panel title={`🚨 Alertes (${data.alerts.length})`} variant="alert" onTitleClick={() => openDrill('alerts', 'all', 'Alertes — détails complets')}>
              {data.alerts.length === 0
                ? <div style={{ padding: 14, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Aucune anomalie 🎉</div>
                : (
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {/* Day 11++ — afișează TOATE alertele cu scroll */}
                    {data.alerts.map((a, i) => (
                      <div key={i} style={alertItemStyle(a.severity)}>
                        <strong>{a.userName}</strong> — {fmtDateFR(a.date)} : {fmtH(a.ts)} encodé (manque {fmtH(a.missing)})
                      </div>
                    ))}
                  </div>
                )}
            </Panel>

            {/* OT DECISIONS — buton declanșare + listă */}
            <Panel title={`⏰ Décisions Heures sup. (${data.otDecisions.length})`} action={
              <button onClick={() => openDrill('ot-decisions', 'all', `Décisions HS — ${labelMonth(month)}`)} style={btnPrimaryMini}>
                👁 Voir les décisions
              </button>
            }>
              {data.otDecisions.length === 0
                ? <div style={{ padding: 14, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Aucun OT à décider</div>
                : (
                  <div style={{ maxHeight: 280, overflowY: 'auto', fontSize: 12 }}>
                    {/* Day 11++ — afișează TOATE deciziile cu scroll */}
                    {data.otDecisions.map((o, i) => (
                      <div key={i} style={otRowStyle(o.status)}>
                        <div style={{ flex: 1 }}>
                          <strong>{o.userName}</strong>
                          <div style={{ fontSize: 10, color: o.monthDelta > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                            {o.monthDelta > 0 ? '+' : ''}{o.monthDelta.toFixed(2)}h ce mois · cumul {o.balanceHours.toFixed(2)}h
                          </div>
                        </div>
                        <span style={{ ...pill, background: o.status === 'decided' ? '#dcfce7' : '#fef3c7', color: o.status === 'decided' ? '#166534' : '#92400e' }}>
                          {o.status === 'decided' ? '✓ Décidé' : '⏳ Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
            </Panel>

            {/* SOURCES — fiecare row clickable */}
            <Panel title="📊 Sources">
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <Source onClick={() => openDrill('ts', 'all', `TS — ${labelMonth(month)}`)} label="⏱ TS encodé" value={data.kpi.totals.tsHoursStr} />
                <Source onClick={() => openDrill('trips', 'all', `Trajets — ${labelMonth(month)}`)} label="🚚 Trajets" value={`${data.kpi.totals.tripsCount} (${fmtEur(data.kpi.totals.tripsEur)})`} />
                <Source onClick={() => openDrill('pallets', 'all', `Palettes — ${labelMonth(month)}`)} label="📦 Palettes" value={`${data.kpi.totals.palletsIn} in / ${data.kpi.totals.palletsOut} out`} />
                <Source onClick={() => openDrill('supplies', 'all', `Prélèvements — ${labelMonth(month)}`)} label="🔄 Prélèv." value={`${data.kpi.totals.suppliesCount}`} />
                <Source onClick={() => openDrill('stockage', 'all', `Stockage sur dalle — ${labelMonth(month)}`)} label="🅿 Stockage dalle"
                  value={data.stockage?.erreur ? 'calcul indisponible'
                    : `${data.kpi.totals.stockageCommandes ? `${fmtEur(data.kpi.totals.stockageMontantCentimes / 100)} (${fmtJp(data.kpi.totals.stockageHeuresPalette)} j·pal)` : '—'}${noteStockage(data)}`}
                  negative={!!data.stockage?.erreur || !!data.stockage?.horsListe || !!data.stockage?.sansLigne} />
                <Source onClick={() => openDrill('inventory', 'all', `Inventaire — ${labelMonth(month)}`)} label="📋 Inventaire" value={`${data.kpi.totals.inventoryCount}`} />
                <Source onClick={() => openDrill('printing', 'all', `Printing — ${labelMonth(month)}`)} label="🖨 Printing" value={`${(data.printingByClient || []).reduce((s, p) => s + p.pages, 0)} pages`} />
                <Source onClick={() => openDrill('errors', 'all', `Erreurs Brain — ${labelMonth(month)}`)} label="🚨 Erreurs Brain" value={data.kpi.totals.errorsBrainCost > 0 ? fmtEur(data.kpi.totals.errorsBrainCost) : `${data.kpi.totals.errorsCount}`} negative={data.kpi.totals.errorsBrainCost > 0} />
              </div>
            </Panel>
          </div>

          {/* TABLE clients — fiecare celulă clickable */}
          <h3 style={{ fontSize: 14, margin: '20px 0 10px', color: '#1e293b' }}>📋 Détail par client (cliquez sur n'importe quelle cellule)</h3>
          <div style={{ overflowX: 'auto', background: 'white', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>Client</th>
                  <th style={{ ...th, textAlign: 'right' }}>TS</th>
                  <th style={{ ...th, textAlign: 'right' }}>Trajets €</th>
                  <th style={{ ...th, textAlign: 'right' }}>km</th>
                  <th style={{ ...th, textAlign: 'right' }}>Palettes</th>
                  <th style={{ ...th, textAlign: 'right' }}>Prélèv.</th>
                  <th style={{ ...th, textAlign: 'right' }}>Stockage</th>
                  <th style={{ ...th, textAlign: 'right' }}>Inventaire</th>
                  <th style={{ ...th, textAlign: 'right' }}>Erreurs Brain</th>
                  <th style={th}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Aucune activité pour ce mois.</td></tr>
                )}
                {data.clients.map(c => (
                  <tr key={c.id}>
                    <td style={td}><strong style={{ color: '#7c3aed' }}>{c.name}</strong><div style={{ fontSize: 10, color: '#94a3b8' }}>{c.kind}</div></td>
                    <td style={{ ...td, ...tdClick }} onClick={() => openDrill('ts', c.id, `TS de ${c.name} — ${labelMonth(month)}`)}>
                      {c.ts.hoursStr}
                      {c.ts.draftCount > 0 && <div style={{ fontSize: 10, color: '#f59e0b' }}>⚠ {c.ts.draftCount} DRAFT</div>}
                    </td>
                    <td style={{ ...td, ...tdClick }} onClick={() => c.trips.count > 0 && openDrill('trips', c.id, `Trajets ${c.name} — ${labelMonth(month)}`)}>
                      {c.trips.count > 0 ? fmtEur(c.trips.eur) : '—'}
                      {c.trips.count > 0 && <div style={{ fontSize: 10, color: '#64748b' }}>{c.trips.count} courses</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: c.trips.km > 0 ? '#1e293b' : '#cbd5e1' }}>{c.trips.km > 0 ? `${c.trips.km} km` : '—'}</td>
                    <td style={{ ...td, ...tdClick, color: c.pallets.total === 0 ? '#cbd5e1' : undefined }} onClick={() => c.pallets.total > 0 && openDrill('pallets', c.id, `Palettes ${c.name}`)}>
                      {c.pallets.total === 0 ? '—' : `${c.pallets.in} / ${c.pallets.out}`}
                      {c.pallets.notInvoiced > 0 && <div style={{ fontSize: 10, color: '#f59e0b' }}>⚠ {c.pallets.notInvoiced} non fact.</div>}
                    </td>
                    <td style={{ ...td, ...tdClick, color: c.supplies.count === 0 ? '#cbd5e1' : undefined }} onClick={() => c.supplies.count > 0 && openDrill('supplies', c.id, `Prélèv. ${c.name}`)}>
                      {c.supplies.count || '—'}
                      {c.supplies.notInvoiced > 0 && <div style={{ fontSize: 10, color: '#f59e0b' }}>⚠ {c.supplies.notInvoiced} non fact.</div>}
                    </td>
                    <td style={{ ...td, ...tdClick, color: c.stockage.commandes === 0 ? '#cbd5e1' : undefined }} onClick={() => c.stockage.commandes > 0 && openDrill('stockage', c.id, `Stockage sur dalle ${c.name} — ${labelMonth(month)}`)}>
                      {c.stockage.commandes === 0 ? '—' : fmtEur(c.stockage.montantCentimes / 100)}
                      {c.stockage.commandes > 0 && (
                        <div style={{ fontSize: 10, color: '#64748b' }}>
                          {fmtJp(c.stockage.heuresPalette)} j·pal{c.stockage.prixJourCentimes ? ` × ${fmtEur(c.stockage.prixJourCentimes / 100)}` : ''}
                        </div>
                      )}
                      {c.stockage.commandes > 0 && data.stockage?.estimation && <div style={{ fontSize: 10, color: '#0369a1' }}>{data.stockage.etat === 'en-attente' ? 'estimation (pas encore calculé)' : 'estimation (mois en cours)'}</div>}
                      {c.stockage.notInvoiced > 0 && <div style={{ fontSize: 10, color: '#f59e0b' }}>⚠ {c.stockage.notInvoiced} non fact.</div>}
                    </td>
                    <td style={{ ...td, ...tdClick, color: c.inventory.count === 0 ? '#cbd5e1' : undefined }} onClick={() => c.inventory.count > 0 && openDrill('inventory', c.id, `Inventaire ${c.name}`)}>
                      {c.inventory.count || '—'}
                      {c.inventory.notInvoiced > 0 && <div style={{ fontSize: 10, color: '#f59e0b' }}>⚠ {c.inventory.notInvoiced} non fact.</div>}
                    </td>
                    <td style={{ ...td, ...tdClick, color: c.errors.brainCount > 0 ? '#dc2626' : '#cbd5e1' }} onClick={() => c.errors.count > 0 && openDrill('errors', c.id, `Erreurs ${c.name}`)}>
                      {c.errors.brainCount > 0 ? (c.errors.brainCost > 0 ? `−${fmtEur(c.errors.brainCost)}` : `${c.errors.brainCount}`) : '—'}
                    </td>
                    <td style={td}>
                      {c.ts.draftCount > 0
                        ? <span style={{ ...pill, background: '#fef3c7', color: '#92400e' }}>⚠ DRAFT</span>
                        : <span style={{ ...pill, background: '#dcfce7', color: '#166534' }}>✓ Prêt</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {data.clients.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#fef3c7', fontWeight: 700 }}>
                    <td style={td}>TOTAL {labelMonth(month)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{data.kpi.totals.tsHoursStr}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtEur(data.kpi.totals.tripsEur)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{data.kpi.totals.tripsKm} km</td>
                    <td style={{ ...td, textAlign: 'right' }}>{data.kpi.totals.palletsIn}/{data.kpi.totals.palletsOut}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{data.kpi.totals.suppliesCount}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtEur(data.kpi.totals.stockageMontantCentimes / 100)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{data.kpi.totals.inventoryCount}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#dc2626' }}>{data.kpi.totals.errorsBrainCost > 0 ? `−${fmtEur(data.kpi.totals.errorsBrainCost)}` : `${data.kpi.totals.errorsCount}`}</td>
                    <td style={td}>{data.kpi.readyCount}/{data.kpi.totalProjects}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* PRINTING separate */}
          {data.printingByClient && data.printingByClient.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '20px 0 10px', color: '#1e293b' }}>🖨 Printing (catalog séparé) — <span style={{ color: '#7c3aed', cursor: 'pointer' }} onClick={() => openDrill('printing', 'all', `Tous les jobs printing — ${labelMonth(month)}`)}>voir tous les jobs</span></h3>
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e2e8f0', overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead><tr><th style={th}>Client printing</th><th style={{ ...th, textAlign: 'right' }}>Jobs</th><th style={{ ...th, textAlign: 'right' }}>Pages</th><th style={{ ...th, textAlign: 'right' }}>Non facturés</th></tr></thead>
                  <tbody>
                    {data.printingByClient.map(pc => (
                      <tr key={pc.clientId}>
                        <td style={td}><strong>{pc.clientLabel}</strong> <span style={{ fontSize: 10, color: '#94a3b8' }}>({pc.clientCode})</span></td>
                        <td style={{ ...td, textAlign: 'right' }}>{pc.jobs}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{pc.pages}</td>
                        <td style={{ ...td, textAlign: 'right', color: pc.notInvoiced > 0 ? '#f59e0b' : '#94a3b8' }}>{pc.notInvoiced || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* LOCK PANEL */}
          {canLock && (
            <div style={lockPanel}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>🔒 Gestion de la période — {labelMonth(month)}</div>
              <div style={{ fontSize: 12, opacity: .85, marginBottom: 10 }}>
                {data.periodLock?.locked
                  ? `VERROUILLÉE depuis le ${fmtDateFR(data.periodLock.lockedAt)}. Déverrouillez pour corrections urgentes.`
                  : 'Période OUVERTE. Verrouillez après validation finale.'}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {data.periodLock?.locked
                  ? <button onClick={handleUnlock} disabled={busyLock} style={{ ...btnUnlock, flex: 1, padding: '10px 16px' }}>🔓 Déverrouiller {labelMonth(month)}</button>
                  : <button onClick={handleLock} disabled={busyLock} style={{ ...btnLock, flex: 1, padding: '10px 16px' }}>🔒 Verrouiller {labelMonth(month)}</button>}
              </div>
            </div>
          )}
        </>
      )}

      {/* DRILL-DOWN MODAL */}
      {drilldown && (
        <DrilldownModal
          yearMonth={month}
          kind={drilldown.kind}
          scope={drilldown.scope}
          title={drilldown.title}
          canDecide={canLock}
          onClose={closeDrill}
          onChange={() => load(month)}
        />
      )}
    </div>
  );
}

// =====================================================================
// DRILL-DOWN MODAL — Shows detail lines for a given kind/scope.
// For 'ot-decisions' kind, includes Payer/Déduire/Reporter buttons.
// =====================================================================
function DrilldownModal({ yearMonth, kind, scope, title, canDecide, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await facturationApi.drilldown(yearMonth, kind, scope)); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [yearMonth, kind, scope]);

  const handleTsAction = async (entry, action) => {
    const desc = `${fmtDateFR(entry.date || entry.timesheet?.date)} ${entry.timesheet?.user?.name || ''} ${entry.projectName}/${entry.taskName} ${fmtH(entry.durationMin)}`;
    try {
      if (action === 'approve') {
        if (!window.confirm(`Approuver TS ?\n${desc}`)) return;
        await facturationApi.approveTs(entry.id);
      } else if (action === 'reject') {
        const reason = window.prompt(`Rejeter TS — motif (obligatoire, min 3 char) :\n${desc}`, '');
        if (!reason?.trim() || reason.trim().length < 3) return;
        await facturationApi.rejectTs(entry.id, reason.trim());
      } else if (action === 'delete') {
        if (!window.confirm(`SUPPRIMER TS définitivement (cascade) ?\n${desc}\n\nCette action est irréversible.`)) return;
        await facturationApi.deleteTs(entry.id);
      }
      await load();
      onChange?.();
    } catch (e) { alert('Erreur: ' + (e?.response?.data?.message || e.message)); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: 900, maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.25)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Kind: <code>{kind}</code> · Scope: <code>{scope}</code></div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontSize: 14 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Chargement…</div>}
          {error && <div style={errorBox}>{error}</div>}
          {data && !loading && (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
                {data.count} ligne(s) trouvée(s)
                {kind === 'stockage' && ({
                  'en-cours': data.verrouille
                    ? ' — mois verrouillé avant sa fin : estimation jusqu\'à maintenant ; le stockage sera calculé une fois le mois terminé.'
                    : ' — mois en cours : estimation jusqu\'à maintenant (tarif actuel), calculée définitivement à la fin du mois.',
                  'en-attente': ' — mois terminé pas encore calculé (le calcul passe dans l\'heure) : estimation au tarif du mois.',
                  incomplet: ' — recalcul en attente (tarif changé, ou ligne Prélèvements pas encore écrite) : prochain passage dans l\'heure.',
                  aucun: ' — aucun tarif de stockage pour ce mois.',
                  futur: '',
                }[data.etat] ?? ' — tarif en vigueur à la fin du mois ; recalculé chaque heure (chargements corrigés) jusqu\'à la clôture du mois, ou jusqu\'à ce que la ligne Prélèvements soit facturée.')}
              </div>
              <DrilldownTable kind={kind} entries={data.entries} canDecide={canDecide} onTsAction={handleTsAction} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== Sub-table per kind ==========
function DrilldownTable({ kind, entries, canDecide, onTsAction }) {
  if (!entries || entries.length === 0) return <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucune ligne.</div>;

  if (kind === 'ts' || kind === 'ts-draft') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Opérateur</th><th style={th}>Projet</th><th style={th}>Tâche</th><th style={th}>Description</th><th style={{ ...th, textAlign: 'right' }}>Durée</th><th style={th}>Statut</th>
      {canDecide && <th style={{ ...th, textAlign: 'right' }}>Actions</th>}
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date || e.timesheet?.date)}</td>
        <td style={td}>{e.timesheet?.user?.name || '—'}</td>
        <td style={td}>{e.projectName}</td>
        <td style={td}>{e.taskName}</td>
        <td style={{ ...td, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.description || '—'}</td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtH(e.durationMin)}</td>
        <td style={td}><span style={{ ...pill, background: e.status === 'APPROVED' ? '#dcfce7' : e.status === 'DRAFT' ? '#fef3c7' : '#fee2e2', color: e.status === 'APPROVED' ? '#166534' : e.status === 'DRAFT' ? '#92400e' : '#991b1b' }}>{e.status}</span></td>
        {canDecide && (
          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {e.status !== 'APPROVED' && (
              <button onClick={() => onTsAction(e, 'approve')} title="Approuver" style={btnTinyOk}>✓</button>
            )}
            {e.status === 'DRAFT' && (
              <button onClick={() => onTsAction(e, 'reject')} title="Rejeter" style={btnTinyWarn}>✗</button>
            )}
            <button onClick={() => onTsAction(e, 'delete')} title="Supprimer (cascade)" style={btnTinyDel}>🗑</button>
          </td>
        )}
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'trips') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Réf</th><th style={th}>Projet</th><th style={th}>Origine → Destination</th><th style={th}>Chauffeur</th><th style={{ ...th, textAlign: 'right' }}>Prix vente</th><th style={{ ...th, textAlign: 'right' }}>km</th><th style={th}>Statut</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.trip?.date)}</td>
        <td style={td}>{e.trip?.ref}</td>
        <td style={td}>{e.project?.name}</td>
        <td style={td}>{e.trip?.origin} → {e.trip?.destination}</td>
        <td style={td}>{e.trip?.driver?.name || '—'}</td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtEur(e.prixVente)}</td>
        <td style={{ ...td, textAlign: 'right' }}>{e.km}</td>
        <td style={td}>{e.trip?.status}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'pallets') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Réf</th><th style={th}>Projet</th><th style={th}>Type</th><th style={{ ...th, textAlign: 'right' }}>In</th><th style={{ ...th, textAlign: 'right' }}>Out</th><th style={th}>Responsable</th><th style={th}>Facturé?</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.ref}</td>
        <td style={td}>{e.project?.name || '—'}</td>
        <td style={td}>{e.palletType?.label} <span style={{ fontSize: 10, color: '#94a3b8' }}>({e.palletType?.code})</span></td>
        <td style={{ ...td, textAlign: 'right' }}>{e.palettesIn}</td>
        <td style={{ ...td, textAlign: 'right' }}>{e.palettesOut}</td>
        <td style={td}>{e.responsible?.name || '—'}</td>
        <td style={td}>{e.invoiced ? '✓' : '—'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'stockage') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Commande</th><th style={th}>Client</th><th style={{ ...th, textAlign: 'right' }}>Palettes</th><th style={th}>Sur la dalle</th><th style={th}>Facturé</th><th style={{ ...th, textAlign: 'right' }}>Jours-palette</th><th style={{ ...th, textAlign: 'right' }}>Prix / jour</th><th style={{ ...th, textAlign: 'right' }}>Montant</th><th style={th}>Prélèv.</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={`${e.orderId}-${e.mois}`}>
        <td style={td}>{e.orderRef}</td>
        <td style={td}>{e.clientName}<div style={{ fontSize: 10, color: '#94a3b8' }}>{e.project?.name}</div></td>
        <td style={{ ...td, textAlign: 'right' }}>{e.palettes}</td>
        <td style={td}>{fmtDateHeure(e.quaiDebut)} → {e.quaiFin ? fmtDateHeure(e.quaiFin) : 'encore là'}</td>
        <td style={td}>{fmtDateHeure(e.factureDebut)} → {fmtDateHeure(e.factureFin)}<div style={{ fontSize: 10, color: '#94a3b8' }}>{e.franchiseJours} j gratuit{e.franchiseJours > 1 ? 's' : ''}</div></td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtJp(e.heuresPalette)}<div style={{ fontSize: 10, color: '#94a3b8' }}>{e.heuresPalette} h</div></td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtEur(e.prixJourCentimes / 100)}</td>
        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtEur(e.montantCentimes / 100)}</td>
        <td style={td}>{e.ligne ? `${e.ligne.ref}${e.ligne.invoiced ? ' ✓' : ''}` : '—'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'supplies') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Réf</th><th style={th}>Projet</th><th style={th}>Type</th><th style={{ ...th, textAlign: 'right' }}>Quantité</th><th style={th}>Encodé par</th><th style={th}>Facturé?</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.ref}</td>
        <td style={td}>{e.project?.name}</td>
        <td style={td}>{e.supplyType?.label} <span style={{ fontSize: 10, color: '#94a3b8' }}>({e.supplyType?.code})</span></td>
        <td style={{ ...td, textAlign: 'right' }}>{e.quantity} {e.supplyType?.unit}</td>
        <td style={td}>{e.createdBy?.name}</td>
        <td style={td}>{e.invoiced ? '✓' : '—'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'inventory') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Réf</th><th style={th}>Item</th><th style={th}>Projet</th><th style={{ ...th, textAlign: 'right' }}>Quantité</th><th style={th}>Compté par</th><th style={th}>Facturé?</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.ref}</td>
        <td style={td}>{e.item?.name} <span style={{ fontSize: 10, color: '#94a3b8' }}>({e.item?.ref})</span></td>
        <td style={td}>{e.item?.project?.name}</td>
        <td style={{ ...td, textAlign: 'right' }}>{String(e.quantity)} {e.item?.unit}</td>
        <td style={td}>{e.countedBy?.name}</td>
        <td style={td}>{e.invoiced ? '✓' : '—'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'errors') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Mvt</th><th style={th}>Opérateur</th><th style={th}>Projet</th><th style={th}>Typologie</th><th style={{ ...th, textAlign: 'right' }}>Coût</th><th style={th}>Resp. Brain</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.movementRef}</td>
        <td style={td}>{e.operator?.name}</td>
        <td style={td}>{e.project?.name}</td>
        <td style={{ ...td, fontSize: 11 }}>{e.typology?.label}</td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtEur(e.cost)}</td>
        <td style={td}>{e.brainResponsibility === 'YES' ? '🚨 OUI' : e.brainResponsibility === 'NO' ? 'Non' : 'TBD'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'printing') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Réf</th><th style={th}>Client</th><th style={th}>Tâche</th><th style={{ ...th, textAlign: 'right' }}>Pages</th><th style={th}>Couleur</th><th style={th}>Papier</th><th style={th}>Facturé?</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.ref}</td>
        <td style={td}>{e.client?.label}</td>
        <td style={td}>{e.taskTemplate?.label}</td>
        <td style={{ ...td, textAlign: 'right' }}>{e.pages}</td>
        <td style={td}>{e.couleur}</td>
        <td style={td}>{e.paperType}</td>
        <td style={td}>{e.invoiced ? '✓' : '—'}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'alerts') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date</th><th style={th}>Opérateur</th><th style={{ ...th, textAlign: 'right' }}>TS encodé</th><th style={{ ...th, textAlign: 'right' }}>Manque</th><th style={th}>Sévérité</th>
    </tr></thead><tbody>{entries.map((e, i) => (
      <tr key={i}>
        <td style={td}>{fmtDateFR(e.date)}</td>
        <td style={td}>{e.userName}</td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtH(e.ts)}</td>
        <td style={{ ...td, textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{fmtH(e.missing)}</td>
        <td style={td}><span style={{ ...pill, background: e.severity === 'high' ? '#fee2e2' : e.severity === 'medium' ? '#fef3c7' : '#dbeafe', color: e.severity === 'high' ? '#991b1b' : e.severity === 'medium' ? '#92400e' : '#1e40af' }}>{e.severity}</span></td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'jf-entries') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Date (JF)</th><th style={th}>Opérateur</th><th style={th}>Projet</th><th style={th}>Tâche</th><th style={{ ...th, textAlign: 'right' }}>Durée</th>
    </tr></thead><tbody>{entries.map(e => (
      <tr key={e.id}>
        <td style={td}>{fmtDateFR(e.date || e.timesheet?.date)}</td>
        <td style={td}>{e.timesheet?.user?.name}</td>
        <td style={td}>{e.projectName}</td>
        <td style={td}>{e.taskName}</td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtH(e.durationMin)}</td>
      </tr>
    ))}</tbody></table>
  );

  if (kind === 'ot-decisions') return (
    <table style={tableStyle}><thead><tr>
      <th style={th}>Opérateur</th><th style={th}>Rôle</th><th style={{ ...th, textAlign: 'right' }}>Ce mois</th><th style={{ ...th, textAlign: 'right' }}>Cumulé</th><th style={th}>Décision</th>
    </tr></thead><tbody>{entries.map(e => {
      const dec = e.decision;
      // Décidé = confirmé par l'employé, forcé par un admin, ou ancien « DECIDED » :
      // calculé par le serveur (une seule liste des statuts).
      const decided = !!dec?.decided;
      return (
        <tr key={e.userId}>
          <td style={td}><strong>{e.userName}</strong></td>
          <td style={td}>{e.role}</td>
          <td style={{ ...td, textAlign: 'right', color: e.monthDelta > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{e.monthDelta > 0 ? '+' : ''}{e.monthDelta.toFixed(2)}h</td>
          <td style={{ ...td, textAlign: 'right' }}>{e.balanceHours.toFixed(2)}h</td>
          <td style={td}>
            {decided
              ? <span style={{ ...pill, background: '#dcfce7', color: '#166534' }}>
                  ✓ {[dec.payHours > 0 && `PAYÉ ${dec.payHours}h`, dec.deductHours > 0 && `DÉDUIT ${dec.deductHours}h`, dec.carryHours !== 0 && `REPORTÉ ${dec.carryHours}h`]
                      .filter(Boolean).join(' · ') || 'RÉGLÉ'}
                </span>
              : <span style={{ ...pill, background: '#fef3c7', color: '#92400e' }}>⏳ Pending</span>}
            {/* 05-10-2026 : « € Payer » / « → Reporter » retirés (demande de Silviu) : ils
                décidaient À LA PLACE de l'employé et écrasaient sa décision. La colonne
                « Actions », devenue vide, est retirée ; l'indication reste ici. */}
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {decided ? `Décidé le ${dec?.decidedAt ? fmtDateFR(aujourdhuiBruxelles(new Date(dec.decidedAt))) : '—'}`   /* jour à Bruxelles */
                : dec?.status === 'PENDING' ? "En attente de la réponse de l'employé (Heures sup.)"
                : e.monthDelta < 0 ? 'REC consommée — auto'
                : 'Se décide dans Temps & Présence › Heures sup.'}
            </div>
          </td>
        </tr>
      );
    })}</tbody></table>
  );

  return <pre style={{ fontSize: 11, padding: 12, background: '#f8fafc', borderRadius: 4 }}>{JSON.stringify(entries, null, 2)}</pre>;
}

// ============== UI Sub-components ==============
function KPI({ label, value, sub, variant, onClick }) {
  const colorMap = {
    success: { bg: '#f0fdf4', border: '#86efac', val: '#166534' },
    warn:    { bg: '#fffbeb', border: '#fbbf24', val: '#92400e' },
    alert:   { bg: '#fef2f2', border: '#fca5a5', val: '#991b1b' },
    normal:  { bg: '#ffffff', border: '#e2e8f0', val: '#1e293b' },
  };
  const c = colorMap[variant || 'normal'];
  return (
    <div onClick={onClick} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: 14, cursor: onClick ? 'pointer' : 'default', transition: 'transform .1s' }}
         onMouseEnter={e => onClick && (e.currentTarget.style.transform = 'translateY(-2px)')}
         onMouseLeave={e => onClick && (e.currentTarget.style.transform = 'none')}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: c.val }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{sub}</div>
    </div>
  );
}
function Panel({ title, children, variant, action, onTitleClick }) {
  const styles = variant === 'alert' ? { border: '1px solid #fca5a5', background: '#fef2f2' } : { border: '1px solid #e2e8f0', background: 'white' };
  return (
    <div style={{ ...styles, borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, cursor: onTitleClick ? 'pointer' : 'default', color: onTitleClick ? '#7c3aed' : '#1e293b' }} onClick={onTitleClick}>
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Source({ label, value, negative, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: '1px dashed #f1f5f9', cursor: onClick ? 'pointer' : 'default',
    }}>
      <span style={{ color: '#475569' }}>{label}</span>
      <strong style={{ color: negative ? '#dc2626' : '#7c3aed', textDecoration: onClick ? 'underline dotted' : 'none' }}>{value}</strong>
    </div>
  );
}
function alertItemStyle(severity) {
  const color = severity === 'high' ? '#fee2e2' : severity === 'medium' ? '#fef3c7' : '#dbeafe';
  const border = severity === 'high' ? '#ef4444' : severity === 'medium' ? '#f59e0b' : '#3b82f6';
  return { padding: '6px 10px', background: color, borderLeft: `3px solid ${border}`, borderRadius: 4, marginBottom: 4, fontSize: 12 };
}
function otRowStyle(status) {
  return {
    padding: '8px 10px',
    background: status === 'decided' ? '#f0fdf4' : '#f8fafc',
    borderRadius: 6,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
}
const btnTinyOk = { padding: '3px 7px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 3, fontSize: 11, cursor: 'pointer', marginLeft: 3 };
const btnTinyWarn = { padding: '3px 7px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 3, fontSize: 11, cursor: 'pointer', marginLeft: 3 };
const btnTinyDel = { padding: '3px 7px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 3, fontSize: 11, cursor: 'pointer', marginLeft: 3 };

// ============== Styles ==============
const selectStyle = { padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: 'white' };
const btnGhost = { padding: '6px 14px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSuccess = { padding: '6px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '6px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: .5 };
const btnLock = { padding: '6px 14px', background: '#1e293b', color: '#fef3c7', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnUnlock = { padding: '6px 14px', background: '#fef3c7', color: '#1e293b', border: '1px solid #1e293b', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnPrimaryMini = { padding: '4px 10px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const pill = { display: 'inline-block', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 };
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #cbd5e1', fontSize: 11, textTransform: 'uppercase', background: '#f8fafc' };
const td = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
const tdClick = { textAlign: 'right', cursor: 'pointer', color: '#7c3aed' };
const errorBox = { background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 };
const lockPanel = { background: '#1e293b', color: '#fef3c7', borderRadius: 10, padding: 14, marginTop: 20 };
